import Head from 'next/head'
import {useRouter} from 'next/router'
import type {JSX, ReactElement} from 'react'
import parse from 'html-react-parser'
import type {
	GlobalConfigContentItem,
	AdvancedPageTemplateContentItem,
	Tersed,
} from '@/services/kontent-ai/types'
import {
	buildRobotsContent,
	getCanonicalUrl,
	validHrefLangs,
} from '@/utilities/seo'
import {env} from '@/services/env/client'

const ResourceHints = (
	<>
		<link href="//assets.elanco.com" rel="preconnect" />
		<link href="//fonts.googleapis.com" rel="dns-prefetch" />
		<link href="//googletagmanager.com" rel="dns-prefetch" />
		<link href="//consent.trustarc.com" rel="dns-prefetch" />
		<link href="//maps.googleapis.com" rel="dns-prefetch" />
		<link href="//google-analytics.com" rel="dns-prefetch" />
		<link href="//connect.facebook.net" rel="dns-prefetch" />
		<link href="//facebook.com" rel="dns-prefetch" />
		<link href="//cdn.taboola.com" rel="dns-prefetch" />
		<link href="//cdns.gigya.com" rel="dns-prefetch" />
		<link href="//cdns.eu1.gigya.com" rel="dns-prefetch" />
		<link href="//accounts.eu1.gigya.com" rel="dns-prefetch" />
		<link href="//googleads.g.doubleclick.net" rel="dns-prefetch" />
		<link href="//assets.elanco.com" rel="dns-prefetch" />
	</>
)

export const PageSEO = ({
	globalConfig,
	page,
	isOnTaxonomyTermPage,
	reqUrl,
	reqHost,
}: {
	globalConfig: Tersed<GlobalConfigContentItem>
	page: Tersed<AdvancedPageTemplateContentItem>
	isOnTaxonomyTermPage: boolean
	reqUrl: string | undefined
	reqHost: string | undefined
}): JSX.Element => {
	const {locale = '', asPath} = useRouter()

	// Environment-based robots override for QA/non-prod environments
	const isProduction = env.NEXT_PUBLIC_NODE_ENV === 'production'

	const robotsContent =
		!isProduction || isOnTaxonomyTermPage
			? 'noindex,nofollow'
			: buildRobotsContent(page, globalConfig)

	// If not in production, override with noindex,nofollow to prevent crawler indexing
	if (!isProduction) {
		const pageTitle = (() => {
			const title = page.elements.pageText || page.elements.pageTitle

			if (title && globalConfig.elements.siteName) {
				return `${title} | ${globalConfig.elements.siteName}`
			}

			if (globalConfig.elements.siteName) {
				return globalConfig.elements.siteName
			}

			return title
		})()
		const baseUrl = `https://${globalConfig.elements.siteHostname}/${locale}`
		const pageURL = `${baseUrl}${
			page.elements.pageUrl === '/' ? '' : page.elements.pageUrl
		}`

		const image = page.elements.snippetPageSeoPageImage[0]?.url
		const contentType = page.system.type
		const hrefLangs =
			validHrefLangs(page.elements.snippetPageSeoPageHreflangs) &&
			(parse(page.elements.snippetPageSeoPageHreflangs) as ReactElement[])

		const isGlobalLocale = locale === 'global'
		let canonicalUrl: string

		if (isGlobalLocale) {
			canonicalUrl =
				typeof window !== 'undefined'
					? window.location.href
					: `https://${reqHost}${reqUrl}`
		} else {
			canonicalUrl = getCanonicalUrl(
				globalConfig.elements.siteHostname,
				locale,
				asPath,
				page.elements.snippetPageSeoPageCanonicalUrl
			)
		}

		return (
			<Head>
				{ResourceHints}
				{!isOnTaxonomyTermPage && (
					<link href={canonicalUrl} rel="canonical" />
				)}
				<meta content={robotsContent} name="robots" />
				<title>{pageTitle}</title>
				<meta content={pageTitle} property="og:title" />
				<meta content={pageURL} property="og:url" />
				{page.elements.snippetPageSeoPageDescription ? (
					<>
						<meta
							content={
								page.elements.snippetPageSeoPageDescription
							}
							name="description"
						/>
						<meta
							content={
								page.elements.snippetPageSeoPageDescription
							}
							property="og:description"
						/>
					</>
				) : null}
				{image ? <meta content={image} name="image" /> : null}
				{contentType ? (
					<meta content={contentType} property="og:type" />
				) : null}
				{image ? <meta content={image} property="og:image" /> : null}
				{hrefLangs}
			</Head>
		)
	}

	const pageTitle = (() => {
		const title = page.elements.pageText || page.elements.pageTitle

		if (title && globalConfig.elements.siteName) {
			return `${title} | ${globalConfig.elements.siteName}`
		}

		if (globalConfig.elements.siteName) {
			return globalConfig.elements.siteName
		}

		return title
	})()
	const baseUrl = `https://${globalConfig.elements.siteHostname}/${locale}`
	const pageURL = `${baseUrl}${
		page.elements.pageUrl === '/' ? '' : page.elements.pageUrl
	}`

	const image = page.elements.snippetPageSeoPageImage[0]?.url
	const contentType = page.system.type
	const hrefLangs =
		validHrefLangs(page.elements.snippetPageSeoPageHreflangs) &&
		(parse(page.elements.snippetPageSeoPageHreflangs) as ReactElement[])

	let canonicalUrl
	if (locale === 'global') {
		if (typeof window !== 'undefined') {
			canonicalUrl = window.location.href
		} else {
			const currentUrl = `https://${reqHost}${reqUrl}`
			canonicalUrl = currentUrl
		}
	} else {
		canonicalUrl = getCanonicalUrl(
			globalConfig.elements.siteHostname,
			locale,
			asPath,
			page.elements.snippetPageSeoPageCanonicalUrl
		)
	}

	return (
		<Head>
			{ResourceHints}
			{!isOnTaxonomyTermPage && (
				<link href={canonicalUrl} rel="canonical" />
			)}
			<meta content={robotsContent} name="robots" />
			<title>{pageTitle}</title>
			<meta content={pageTitle} property="og:title" />
			<meta content={pageURL} property="og:url" />
			{page.elements.snippetPageSeoPageDescription ? (
				<>
					<meta
						content={page.elements.snippetPageSeoPageDescription}
						name="description"
					/>
					<meta
						content={page.elements.snippetPageSeoPageDescription}
						property="og:description"
					/>
				</>
			) : null}
			{image ? <meta content={image} name="image" /> : null}
			{contentType ? (
				<meta content={contentType} property="og:type" />
			) : null}
			{image ? <meta content={image} property="og:image" /> : null}
			{hrefLangs}
		</Head>
	)
}
